import { useCallback, useEffect, useState } from 'react'
import { userCategoriesApi, type UserCategory } from '../api/team'
import { CloneConfigurationButton } from './CloneConfigurationButton'
import { Button, Callout, Card, CardContent, CardHeader, List, ListItem } from './ui'

/**
 * F-163 (REQ-257-001): pannello dell'Admin sui ruoli. Mostra TUTTE le categorie (anche le inattive, create ad esempio
 * dai template) con il pulsante "Attiva" e, per ognuna, "Copia" (la copia nasce inattiva e non di sistema). Una
 * categoria inattiva non da' permessi finche' non viene attivata. In questo giro non c'e' "Disattiva".
 * Va mostrato dietro il flag `ruoli.attivazione_copia`.
 */
/** `onChanged` (F-165): chiamata dopo un'attivazione o una copia, per aggiornare la tabella della pagina che ospita il pannello. */
export function UserCategoryAdminPanel({ onChanged }: { onChanged?: () => void } = {}) {
  const [items, setItems] = useState<UserCategory[]>([])
  const [error, setError] = useState('')
  const [busyId, setBusyId] = useState<number | null>(null)

  const load = useCallback(async () => {
    setError('')
    try {
      setItems(await userCategoriesApi.list({ includeInactive: true }))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore durante il caricamento')
    }
  }, [])

  useEffect(() => { void load() }, [load])

  async function activate(id: number) {
    setBusyId(id)
    setError('')
    try {
      await userCategoriesApi.update(id, { isActive: true })
      await load()
      onChanged?.()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Attivazione non riuscita')
    } finally {
      setBusyId(null)
    }
  }

  const inactive = items.filter(c => !c.isActive)

  return (
    <Card variant="outlined">
      <CardHeader
        title="Attivazione e copia dei ruoli"
        subtitle="Una categoria inattiva non dà permessi. Le categorie create dai template nascono inattive: attivale qui dopo averle controllate."
      />
      <CardContent>
        <div className="md-stack">
          {error && <Callout tone="error">{error}</Callout>}

          <h3 className="md-typescale-title-small">Categorie inattive ({inactive.length})</h3>
          {inactive.length === 0 ? (
            <p className="md-typescale-body-medium text-muted">Nessuna categoria inattiva.</p>
          ) : (
            <List aria-label="Categorie inattive">
              {inactive.map(c => (
                <ListItem
                  key={c.id}
                  headline={c.name}
                  supporting={c.slug}
                  trailing={
                    <Button variant="tonal" disabled={busyId === c.id} loading={busyId === c.id} onClick={() => void activate(c.id)}>
                      Attiva
                    </Button>
                  }
                />
              ))}
            </List>
          )}

          <h3 className="md-typescale-title-small">Copia di un ruolo</h3>
          <List aria-label="Copia di un ruolo">
            {items.map(c => (
              <ListItem
                key={c.id}
                overline={c.isActive ? undefined : 'Inattiva'}
                headline={c.name}
                supporting={c.slug}
                trailing={
                  <CloneConfigurationButton
                    entityLabel="ruolo"
                    url={`/api/usercategories/${c.id}/clone`}
                    defaultName={c.name}
                    extraFields={[{ key: 'slug', label: 'Slug della copia', required: true, placeholder: `${c.slug}-copia` }]}
                    note="La copia nasce inattiva e non di sistema, con gli stessi permessi: va attivata a mano."
                    onCloned={() => { void load(); onChanged?.() }}
                  />
                }
              />
            ))}
          </List>
        </div>
      </CardContent>
    </Card>
  )
}
